'use client';

import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { ScheduleMatchupPeriodsDto } from '@/infrastructure/network/dto/ScheduleMatchupPeriodsDto';
import { useTeamLeagueStore } from '@/store/team-league-store';
import { formatDateRangeWithYear, formatShortDateRange } from '@/utils/date';
import { formatTeamLeagueLabels } from '@/utils/helpers';

interface SchedulePeriodDropdownProps {
	periods?: ScheduleMatchupPeriodsDto[];
	setStartDate: (date: string) => void;
	setEndDate: (date: string) => void;
	isMonthView?: boolean;
	updateParams?: boolean;
	hideStatusDropdown?: boolean;
}

export function SchedulePeriodDropdown({
	periods,
	setStartDate,
	setEndDate,
	isMonthView,
	updateParams = true,
	hideStatusDropdown = false
}: SchedulePeriodDropdownProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const statusDropdownRef = useRef<HTMLDivElement | null>(null);
	const activeParam = searchParams.get('active');
	const initialStatus = activeParam === 'false' ? 'past' : 'active';
	const pathname = usePathname();

	const [status, setStatus] = useState<'active' | 'past'>(initialStatus);

	const teamLeagueInfo = useTeamLeagueInfo();
	const { setScheduleGroupUuid, scheduleGroupUuid } = useTeamLeagueStore();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	const urlStartDate = searchParams.get('startDate');
	const urlEndDate = searchParams.get('endDate');

	const [selectedPeriod, setSelectedPeriod] = useState<ScheduleMatchupPeriodsDto | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

	useEffect(() => {
		if (periods && periods.length === 0) {
			setSelectedPeriod(null);
			setStartDate('');
			setEndDate('');
			setScheduleGroupUuid('');
		}
		if (periods && periods.length > 0) {
			const firstPeriod = periods[0];
			const initialPeriod = periods.find((p) => p.startDate === urlStartDate && p.endDate === urlEndDate) ?? firstPeriod;

			setSelectedPeriod(initialPeriod || null);
			setStartDate(initialPeriod?.startDate || '');
			setEndDate(initialPeriod?.endDate || '');

			if (initialPeriod?.scheduleGroupUuid) {
				setScheduleGroupUuid(initialPeriod.scheduleGroupUuid);
			}
		}
		return () => {
			if (scheduleGroupUuid) setScheduleGroupUuid('');
		};
	}, [periods, urlStartDate, urlEndDate]);

	const handleStatusChange = (newStatus: 'active' | 'past') => {
		setStatus(newStatus);
		const params = new URLSearchParams(searchParams.toString());
		params.set('active', newStatus === 'active' ? 'true' : 'false');

		router.replace(`${pathname}?${params.toString()}`, { scroll: false });
	};

	const handleSelect = (period: ScheduleMatchupPeriodsDto) => {
		setScheduleGroupUuid(period.scheduleGroupUuid);
		setSelectedPeriod(period);
		setStartDate(period.startDate);
		setEndDate(period.endDate);

		const params = new URLSearchParams(searchParams.toString());
		params.set('startDate', period.startDate);
		params.set('endDate', period.endDate);
		params.set('period', period.scheduleGroupUuid);

		if (updateParams) {
			router.replace(`${pathname}?${params.toString()}`, { scroll: false });
		}

		setIsOpen(false);
	};

	const toggleDropdown = () => {
		setIsStatusDropdownOpen(false);
		setIsOpen((prev) => !prev);
	};

	const toggleStatusDropdown = () => {
		setIsOpen(false);
		setIsStatusDropdownOpen((prev) => !prev);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
			if (statusDropdownRef.current && event.target instanceof Node && !statusDropdownRef.current.contains(event.target)) {
				setIsStatusDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div ref={dropdownRef} className="relative w-full md:w-[348px]">
			<div className="flex gap-2 sm:gap-0 flex-col sm:flex-row">
				<button
					onClick={toggleDropdown}
					className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-normal text-gray-700 shadow-sm focus:outline-none"
				>
					{selectedPeriod ? (
						<span className="flex items-center gap-2 w-[290px] overflow-hidden min-w-0">
							{selectedPeriod.scheduleGroupTitle ? (
								<>
									<span className="text-gray-500 text-sm truncate">{selectedPeriod.scheduleGroupTitle}</span>
									<span className="text-gray-500 text-sm whitespace-nowrap flex-shrink-0">
										(
										{!periods || periods.length === 0
											? `No matches`
											: `${selectedPeriod.numberOfMatchups}  ${labels.matchupPluralTitle}`}
										)
									</span>
								</>
							) : !periods || periods.length === 0 ? (
								<span className="text-gray-500 text-sm">No matches</span>
							) : (
								<>
									<span className="text-gray-700 font-semibold text-sm truncate items-start">
										{formatShortDateRange(selectedPeriod.startDate, selectedPeriod.endDate, isMonthView)}
									</span>
									<span className="text-gray-500 text-xs whitespace-nowrap flex-shrink-0">
										({selectedPeriod.numberOfMatchups} {labels.matchupPluralTitle})
									</span>
								</>
							)}
						</span>
					) : (
						<span className="text-sm">Select period</span>
					)}

					<FontAwesomeIcon
						icon={faAngleDown}
						className={`ml-2 size-4 flex-shrink-0 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
						aria-hidden="true"
					/>
				</button>

				{/* {status dropdown} */}
				{teamLeagueInfo.useScheduleGroups && !hideStatusDropdown ? (
					<div ref={statusDropdownRef} className="relative w-[120px] ml-0 sm:ml-2">
						<button
							onClick={toggleStatusDropdown}
							className="w-full flex justify-between items-center border border-gray-300 bg-white px-4 py-2 text-sm rounded shadow-sm"
						>
							<span className="capitalize">{status}</span>
							<FontAwesomeIcon
								icon={faAngleDown}
								className={`ml-2 size-4 text-gray-400 transition-transform duration-300 ${isStatusDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
							/>
						</button>

						{isStatusDropdownOpen ? (
							<div className="absolute mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg z-10">
								{['active', 'past'].map((s) => (
									<button
										key={s}
										className={`w-full px-4 py-2 text-left text-sm capitalize
										${s === status ? 'bg-orange-100  font-semibold' : ''}
										hover:bg-orange-50`}
										onClick={() => {
											handleStatusChange(s as 'active' | 'past');
											setIsStatusDropdownOpen(false);
										}}
									>
										{s}
									</button>
								))}
							</div>
						) : null}
					</div>
				) : null}
			</div>

			{isOpen ? (
				<div className="absolute max-h-60 overflow-x-auto left-0 top-9 sm:top-full z-10 mt-1 w-full rounded-md border border-gray-200 bg-white p-2 shadow-lg">
					{!periods || periods.length === 0 ? (
						<div className="px-4 py-2 text-sm text-gray-500">No matches</div>
					) : (
						periods.map((period, index) => (
							<button
								key={`${period.poolUuid}_${period.startDate}_${period.endDate}_${index}`}
								onClick={() => handleSelect(period)}
								className={`w-full text-left p-2 rounded-md flex flex-col ${
									selectedPeriod?.startDate === period.startDate &&
									selectedPeriod?.endDate === period.endDate &&
									selectedPeriod.scheduleGroupUuid === period.scheduleGroupUuid
										? 'bg-[#FCD3B0]'
										: 'hover:bg-[#FEE8D2]'
								}`}
							>
								<div className="flex justify-between items-center">
									{period.scheduleGroupTitle ? <span className="font-bold text-lg">{period.scheduleGroupTitle}</span> : null}
									{period.scheduleGroupTitle ? (
										<span className="font-normal text-xs">{formatDateRangeWithYear(period.startDate, period.endDate)}</span>
									) : null}
									{!period.scheduleGroupTitle && (
										<span className="font-bold text-sm">
											{formatShortDateRange(period.startDate, period.endDate, isMonthView)}
										</span>
									)}
								</div>

								<span className="text-xs text-gray-500">
									{period.numberOfMatchups} {labels.matchupPluralTitle}
								</span>
							</button>
						))
					)}
				</div>
			) : null}
		</div>
	);
}
